import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Tooltip,
  Typography,
} from '@mui/material';
import type { PaperProps } from '@mui/material';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Category } from '../../types/Category';
import {
  categoryPickerNodeExpandable,
  collectAddMatchingContentSourcesAlongAncestorPath,
  contentCategoryMayHaveRows,
  contentCountForPickerDisplay,
  findCategoryInTree,
  findDocumentRootCategory,
  type AddMatchingContentSourceGroup,
} from '../../utils/CategoryUtils';
import {
  branchChildrenAreLoaded,
  categoryContentsAreLoaded,
} from '../../utils/categoryLoadState';
import { CategoryPickerTreeLabel } from './CompareContentDialog';
import { Content } from '../../types/Content';
import api from '../../services/api';
import {
  AddMatchingContentMatchPreview,
  applyAddMatchingContent,
  fetchCategoryForPane,
  mapAddMatchingContentTransfers,
  mergeBranchFetchIntoTree,
  mergeContentsFetchIntoTree,
  previewAddMatchingContent,
} from '../../services/CategoryService';
import { useSearchSettings } from '../../contexts/SearchSettingsContext';
import { useHelpContent } from '../../utils/HelpUtils';
import ContentRichPreviewDialog from '../content/ContentRichPreviewDialog';
import AdjustTextDialog from './AdjustTextDialog';
import { prepareContentRichPreviewHtml } from '../../utils/contentRichPreviewHtml';

export interface AddMatchingContentTransfer {
  contentId: string;
  sourceContentId?: string;
  sourceCategoryId: string;
  targetCategoryId: string;
  moved: boolean;
}

/**
 * Dialog Paper that can be dragged by an element marked with data-drag-handle="true" (the title),
 * so the user can move the dialog aside to view the category tree. Defined at module scope so it is
 * a stable component type (a PaperComponent redefined per render would remount the dialog content).
 */
const DraggablePaper: React.FC<PaperProps> = (props) => {
  // Start shifted toward the right so the dialog doesn't cover the (left-side) category tree.
  // Approximate the maxWidth="sm" paper (~600px) and clamp so it stays fully on screen.
  const [pos, setPos] = useState(() => {
    if (typeof window === 'undefined') return { x: 0, y: 0 };
    const maxRightOffset = Math.max(0, (window.innerWidth - 600) / 2 - 16);
    return { x: Math.round(maxRightOffset * 0.5), y: 0 };
  });
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      setPos({
        x: drag.origX + (e.clientX - drag.startX),
        y: drag.origY + (e.clientY - drag.startY),
      });
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('[data-drag-handle="true"]')) return;
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
    };
  };

  return (
    <Paper
      {...props}
      onMouseDown={handleMouseDown}
      style={{ ...props.style, transform: `translate(${pos.x}px, ${pos.y}px)` }}
    />
  );
};

function collectAncestorCategoryIds(categoriesTree: Category[], categoryId: string): string[] {
  const ids: string[] = [];
  let parentId = findCategoryInTree(categoriesTree, categoryId)?.parentId;
  while (parentId) {
    ids.push(parentId);
    parentId = findCategoryInTree(categoriesTree, parentId)?.parentId;
  }
  return ids;
}

interface AddMatchingContentDialogProps {
  open: boolean;
  onClose: () => void;
  targetCategory: Category | null;
  categoriesTree: Category[];
  sourceGroups: AddMatchingContentSourceGroup[];
  defaultSourceId?: string | null;
  suggestedSourceExamples?: string[];
  onSuccess: (targetCategoryId: string, transfers: AddMatchingContentTransfer[], move: boolean) => void;
  /**
   * Notifies the parent that a source row's text was edited in place, with the previous row state,
   * so it can sync its tree and record an undoable history entry.
   */
  onSourceRowEdited?: (categoryId: string, updated: Content, previous?: Content) => void;
}

const CHOOSE_SOURCE_MESSAGE = 'Choose a category to copy or move content from.';

type DialogStep = 'setup' | 'review';
type ApplyMode = 'all' | 'step' | 'quick';

const matchPreviewText = (
  match: AddMatchingContentMatchPreview,
  content: Content | undefined,
  maxLen = 200,
): string => {
  const raw = content?.text?.trim() || match.textPreview?.trim() || '(empty)';
  if (raw.length <= maxLen) return raw;
  return `${raw.slice(0, maxLen)}…`;
};

const formatMatchSimilarityPercent = (score: number | undefined): string | null => {
  if (score === undefined || Number.isNaN(score)) return null;
  return `${(score * 100).toFixed(1)}%`;
};

const DEFAULT_FIND_SIMILARITY_THRESHOLD = 0.7;

const clampSimilarityThreshold = (value: number): number => {
  if (!Number.isFinite(value)) return DEFAULT_FIND_SIMILARITY_THRESHOLD;
  return Math.min(1, Math.max(0, value));
};

function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object' && 'message' in data) {
      const msg = (data as { message?: unknown }).message;
      if (typeof msg === 'string' && msg.trim()) return msg;
    }
  }
  return fallback;
}

const AddMatchingContentDialog: React.FC<AddMatchingContentDialogProps> = ({
  open,
  onClose,
  targetCategory,
  categoriesTree,
  sourceGroups,
  defaultSourceId,
  suggestedSourceExamples,
  onSuccess,
  onSourceRowEdited,
}) => {
  const { settings, updateSettings } = useSearchSettings();
  const { addMatchingShowSimilarityScore } = settings;
  const { getTooltipTextForState } = useHelpContent();
  const [step, setStep] = useState<DialogStep>('setup');
  const [similarityThreshold, setSimilarityThreshold] = useState(
    clampSimilarityThreshold(settings.findSimilarityThreshold),
  );
  const [sourceCategoryId, setSourceCategoryId] = useState('');
  const [moveMode, setMoveMode] = useState<'copy' | 'move'>('copy');
  const [applyMode, setApplyMode] = useState<ApplyMode>('step');
  const [quickSelectedIds, setQuickSelectedIds] = useState<Set<string>>(new Set());
  const [allMatches, setAllMatches] = useState<AddMatchingContentMatchPreview[]>([]);
  const [selectedSubcases, setSelectedSubcases] = useState<Set<string>>(new Set());
  const [reviewIndex, setReviewIndex] = useState(0);
  // Step-by-step decisions are deferred: recorded per source row id and applied in one
  // batch at the end, so the user can move back/forward and change their mind freely.
  const [decisions, setDecisions] = useState<Map<string, 'accepted' | 'skipped'>>(new Map());
  const [sourceContentsById, setSourceContentsById] = useState<Map<string, Content>>(new Map());
  // Source rows whose text was edited in place during this review (for the "Edited" marker), plus a
  // snapshot of each one's original text so the edit can be reverted. Edits are persisted on Save.
  const [editedRowIds, setEditedRowIds] = useState<Set<string>>(new Set());
  const [originalTextById, setOriginalTextById] = useState<Map<string, string>>(new Map());
  const [stitchOpen, setStitchOpen] = useState(false);
  const [stitchRowId, setStitchRowId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [richPreviewOpen, setRichPreviewOpen] = useState(false);
  const [confirmApplyOpen, setConfirmApplyOpen] = useState(false);
  const [pickerTree, setPickerTree] = useState<Category[]>([]);
  const [expandedNodeIds, setExpandedNodeIds] = useState<string[]>([]);
  const [treeLoadingIds, setTreeLoadingIds] = useState<Set<string>>(new Set());
  /** Threshold that produced the current match count (slider can move before rematch). */
  const [previewedThreshold, setPreviewedThreshold] = useState<number | null>(null);
  const previewRequestGenRef = useRef(0);
  /** Bumps on each dialog open/init so stale async work from Strict Mode or a prior open is ignored. */
  const initGenRef = useRef(0);
  const pickerTreeRef = useRef<Category[]>([]);
  pickerTreeRef.current = pickerTree;
  const similarityThresholdRef = useRef(similarityThreshold);
  similarityThresholdRef.current = similarityThreshold;
  /** Saved Settings default captured when this dialog session opened (Reset restores this). */
  const sessionDefaultThresholdRef = useRef(
    clampSimilarityThreshold(settings.findSimilarityThreshold),
  );

  const matchSubcases = useMemo(() => {
    const seen = new Set<string>();
    for (const m of allMatches) seen.add(m.subcase ?? '');
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [allMatches]);

  const showSubcaseFilter = matchSubcases.length >= 2;

  useEffect(() => {
    setSelectedSubcases(new Set(matchSubcases));
  }, [matchSubcases]);

  const matches = useMemo(() => {
    if (!showSubcaseFilter) return allMatches;
    return allMatches.filter((m) => selectedSubcases.has(m.subcase ?? ''));
  }, [allMatches, selectedSubcases, showSubcaseFilter]);

  const documentRoot = useMemo(
    () =>
      targetCategory ? findDocumentRootCategory(pickerTree, targetCategory.id) : null,
    [pickerTree, targetCategory],
  );

  /** Load content rows for the given content-category ids so picker labels can show counts. */
  const hydrateContentCategoryCounts = useCallback(async (categoryIds: string[]) => {
    const toLoad = categoryIds.filter((id) => {
      const live = findCategoryInTree(pickerTreeRef.current, id);
      return (
        !!live?.contentCategory &&
        !categoryContentsAreLoaded(live) &&
        contentCategoryMayHaveRows(live)
      );
    });
    if (toLoad.length === 0) return;

    setTreeLoadingIds((prev) => {
      const next = new Set(prev);
      toLoad.forEach((id) => next.add(id));
      return next;
    });
    try {
      const results = await Promise.all(
        toLoad.map(async (id) => {
          try {
            return { id, loaded: await fetchCategoryForPane(id) };
          } catch {
            return null;
          }
        }),
      );
      setPickerTree((prev) => {
        let next = prev;
        for (const result of results) {
          if (!result) continue;
          next = mergeContentsFetchIntoTree(next, result.id, result.loaded);
        }
        pickerTreeRef.current = next;
        return next;
      });
    } finally {
      setTreeLoadingIds((prev) => {
        const next = new Set(prev);
        toLoad.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, []);

  const ensurePickerNodeLoaded = useCallback(async (categoryId: string) => {
    const live = findCategoryInTree(pickerTreeRef.current, categoryId);
    if (!live) return null;
    if (live.contentCategory) {
      if (categoryContentsAreLoaded(live)) return live;
      setTreeLoadingIds((prev) => new Set(prev).add(categoryId));
      try {
        const loaded = await fetchCategoryForPane(categoryId);
        setPickerTree((prev) => {
          const next = mergeContentsFetchIntoTree(prev, categoryId, loaded);
          pickerTreeRef.current = next;
          return next;
        });
        return loaded;
      } finally {
        setTreeLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(categoryId);
          return next;
        });
      }
    }
    if (branchChildrenAreLoaded(live)) return live;
    setTreeLoadingIds((prev) => new Set(prev).add(categoryId));
    try {
      const loaded = await fetchCategoryForPane(categoryId);
      setPickerTree((prev) => {
        const next = mergeBranchFetchIntoTree(prev, categoryId, loaded);
        pickerTreeRef.current = next;
        return next;
      });
      // Load row counts for content categories now visible under this branch.
      const contentIds = (loaded.children ?? [])
        .filter((c) => c.contentCategory && contentCategoryMayHaveRows(c))
        .map((c) => c.id);
      if (contentIds.length > 0) {
        void hydrateContentCategoryCounts(contentIds);
      }
      return loaded;
    } finally {
      setTreeLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(categoryId);
        return next;
      });
    }
  }, [hydrateContentCategoryCounts]);

  const refreshPreview = useCallback(async (targetId: string, sourceId: string, threshold?: number) => {
    if (!targetId || !sourceId || sourceId === targetId) {
      setAllMatches([]);
      setPreviewedThreshold(null);
      setError(null);
      return;
    }
    const requestGen = ++previewRequestGenRef.current;
    const usedThreshold = clampSimilarityThreshold(
      threshold ?? similarityThresholdRef.current,
    );
    setPreviewLoading(true);
    setError(null);
    try {
      // Preview is server-side (category ids only); client tree load state does not matter.
      const data = await previewAddMatchingContent(targetId, sourceId, usedThreshold);
      if (requestGen !== previewRequestGenRef.current) {
        return;
      }
      const list = Array.isArray(data.matches) ? data.matches : [];
      setAllMatches(list);
      setPreviewedThreshold(usedThreshold);
    } catch (err: unknown) {
      if (requestGen !== previewRequestGenRef.current) {
        return;
      }
      setAllMatches([]);
      setPreviewedThreshold(usedThreshold);
      const raw = extractApiErrorMessage(err, 'Failed to preview matching content.');
      if (
        raw.includes('required') ||
        raw.toLowerCase().includes('not found') ||
        raw.toLowerCase().includes('no content')
      ) {
        setError(CHOOSE_SOURCE_MESSAGE);
      } else {
        setError(raw);
      }
    } finally {
      if (requestGen === previewRequestGenRef.current) {
        setPreviewLoading(false);
      }
    }
  }, []);

  /** Select a content category as source and immediately load match preview. */
  const selectSourceCategory = useCallback(
    async (sourceId: string) => {
      if (!targetCategory || !sourceId || sourceId === targetCategory.id) return;
      if (sourceId.endsWith('__loading')) return;
      const cat = findCategoryInTree(pickerTreeRef.current, sourceId);
      // Branches are navigation only.
      if (cat && !cat.contentCategory) return;
      // Do not select known-empty content categories.
      if (cat?.contentCategory && !contentCategoryMayHaveRows(cat)) return;

      setError(null);
      setSourceCategoryId(sourceId);
      setPreviewedThreshold(null);
      setAllMatches([]);
      // Load contents in the background for row counts in the tree label.
      if (!cat || (cat.contentCategory && !categoryContentsAreLoaded(cat))) {
        void ensurePickerNodeLoaded(sourceId);
      }
      await refreshPreview(targetCategory.id, sourceId);
    },
    [targetCategory, ensurePickerNodeLoaded, refreshPreview],
  );

  /** Local override only — does not persist to Settings. Rematches when a source is selected. */
  const applySimilarityThreshold = (raw: number | number[]) => {
    const next = clampSimilarityThreshold(Array.isArray(raw) ? raw[0] : raw);
    setSimilarityThreshold(next);
    similarityThresholdRef.current = next;
    if (targetCategory && sourceCategoryId) {
      void refreshPreview(targetCategory.id, sourceCategoryId, next);
    }
  };

  const handleSourceTreeSelect = (_event: React.SyntheticEvent, nodeIdInput: string | string[]) => {
    const nodeId = Array.isArray(nodeIdInput) ? nodeIdInput[0] : nodeIdInput;
    if (!nodeId || nodeId.endsWith('__loading')) return;
    void selectSourceCategory(nodeId);
  };

  const handleNodeToggle = (_event: React.SyntheticEvent, nodeIds: string[]) => {
    setExpandedNodeIds(nodeIds);
    const newlyExpanded = nodeIds.filter((id) => !expandedNodeIds.includes(id));
    for (const id of newlyExpanded) {
      const cat = findCategoryInTree(pickerTreeRef.current, id);
      if (cat && !cat.contentCategory && !branchChildrenAreLoaded(cat)) {
        void ensurePickerNodeLoaded(id);
      } else if (cat && !cat.contentCategory && branchChildrenAreLoaded(cat)) {
        // Branch already loaded — still ensure visible content children have counts.
        const contentIds = (cat.children ?? [])
          .filter((c) => c.contentCategory && contentCategoryMayHaveRows(c))
          .map((c) => c.id);
        if (contentIds.length > 0) {
          void hydrateContentCategoryCounts(contentIds);
        }
      }
    }
  };

  // Whenever the expanded path changes, load row counts for every visible content category.
  useEffect(() => {
    if (!open || pickerTree.length === 0) return;
    const expanded = new Set(expandedNodeIds);
    const ids: string[] = [];
    const walk = (cat: Category, parentExpanded: boolean) => {
      if (cat.contentCategory) {
        if (
          parentExpanded &&
          contentCategoryMayHaveRows(cat) &&
          !categoryContentsAreLoaded(cat)
        ) {
          ids.push(cat.id);
        }
        return;
      }
      if (!expanded.has(cat.id) || !cat.children) return;
      for (const child of cat.children) {
        walk(child, true);
      }
    };
    for (const root of pickerTree) {
      // Root is always shown; its children are visible when the root is expanded.
      if (root.contentCategory) {
        walk(root, true);
      } else if (expanded.has(root.id)) {
        for (const child of root.children ?? []) {
          walk(child, true);
        }
      }
    }
    if (ids.length > 0) {
      void hydrateContentCategoryCounts(ids);
    }
  }, [open, pickerTree, expandedNodeIds, hydrateContentCategoryCounts]);

  const renderSourceTreeNode = (category: Category): React.ReactNode | null => {
    if (!targetCategory) return null;
    const isDestination = category.id === targetCategory.id;
    const isContent = !!category.contentCategory;
    // Only non-empty content categories (and the destination marker) belong in this picker.
    if (isContent && !isDestination && !contentCategoryMayHaveRows(category)) {
      return null;
    }
    // Any non-empty content category (except the destination) can be chosen as source.
    const selectable =
      isContent && !isDestination && contentCategoryMayHaveRows(category);
    const expandable = categoryPickerNodeExpandable(category);
    const childrenLoaded = branchChildrenAreLoaded(category);
    const hasLoadedChildren = (category.children?.length ?? 0) > 0;
    // Branches: show when expandable or already has loaded children.
    if (
      !isContent &&
      !isDestination &&
      !expandable &&
      !hasLoadedChildren
    ) {
      return null;
    }
    const loading = treeLoadingIds.has(category.id);
    const rowCount = contentCountForPickerDisplay(category);
    let nameSuffix = '';
    if (isDestination) {
      nameSuffix = ' (destination)';
    } else if (selectable && defaultSourceId === category.id) {
      nameSuffix = ' (suggested)';
    } else if (loading) {
      nameSuffix = ' (loading…)';
    }
    const nameColor = isDestination
      ? 'text.disabled'
      : selectable
        ? 'text.primary'
        : 'text.secondary';

    const label = (
      <Box
        onMouseDown={
          isContent && !isDestination
            ? (e) => {
                // Use mousedown so we run before TreeView handles the event.
                e.preventDefault();
                e.stopPropagation();
                void selectSourceCategory(category.id);
              }
            : undefined
        }
        sx={isContent && !isDestination ? { cursor: 'pointer', width: '100%' } : undefined}
      >
        <CategoryPickerTreeLabel
          category={category}
          contentCount={rowCount}
          nameColor={nameColor}
          nameSuffix={nameSuffix}
        />
      </Box>
    );

    // Content categories must not pass children — MUI shows a chevron for any child nodes,
    // including `false` from `condition && …` expressions.
    if (isContent) {
      return (
        <TreeItem
          key={category.id}
          nodeId={category.id}
          label={label}
          disabled={isDestination}
        />
      );
    }

    return (
      <TreeItem
        key={category.id}
        nodeId={category.id}
        label={label}
        disabled={false}
      >
        {hasLoadedChildren
          ? category.children!.map((child) => renderSourceTreeNode(child)).filter(Boolean)
          : null}
        {expandable && !childrenLoaded && !hasLoadedChildren ? (
          <TreeItem
            nodeId={`${category.id}__loading`}
            label={
              <Typography variant="body2" color="text.secondary">
                Loading…
              </Typography>
            }
            disabled
          />
        ) : null}
      </TreeItem>
    );
  };

  const resetReviewState = useCallback(() => {
    setStep('setup');
    setReviewIndex(0);
    setDecisions(new Map());
    setSourceContentsById(new Map());
    setEditedRowIds(new Set());
    setOriginalTextById(new Map());
    setStitchOpen(false);
    setStitchRowId(null);
    setRichPreviewOpen(false);
    setConfirmApplyOpen(false);
    setQuickSelectedIds(new Set());
  }, []);

  useEffect(() => {
    if (!open || !targetCategory) {
      return;
    }

    const gen = ++initGenRef.current;
    const target = targetCategory;
    const treeSnapshot = categoriesTree;
    const defaultId = defaultSourceId;
    const groupsSnapshot = sourceGroups;

    setMoveMode('copy');
    setApplyMode('step');
    setError(null);
    resetReviewState();
    setAllMatches([]);
    setPreviewedThreshold(null);
    setSourceCategoryId('');
    setPreviewLoading(false);
    setTreeLoadingIds(new Set());
    // Always start this session at the saved Settings default (do not keep a prior override).
    const fallbackThreshold = clampSimilarityThreshold(settings.findSimilarityThreshold);
    setSimilarityThreshold(fallbackThreshold);
    similarityThresholdRef.current = fallbackThreshold;
    sessionDefaultThresholdRef.current = fallbackThreshold;

    void (async () => {
      let initialThreshold = fallbackThreshold;
      try {
        const response = await api.get('/search/settings');
        const backend = (response.data as { search?: { findSimilarityThreshold?: number } })
          ?.search?.findSimilarityThreshold;
        if (typeof backend === 'number' && Number.isFinite(backend)) {
          initialThreshold = clampSimilarityThreshold(backend);
        }
      } catch {
        // Keep the in-memory Settings value if the fetch fails.
      }
      if (gen !== initGenRef.current) return;
      setSimilarityThreshold(initialThreshold);
      similarityThresholdRef.current = initialThreshold;
      sessionDefaultThresholdRef.current = initialThreshold;

      let tree = treeSnapshot;
      setPickerTree(tree);
      pickerTreeRef.current = tree;

      const ancestorIds = collectAncestorCategoryIds(tree, target.id);
      const root = findDocumentRootCategory(tree, target.id);
      const initialExpanded = new Set<string>();
      if (root) initialExpanded.add(root.id);
      initialExpanded.add(target.id);
      ancestorIds.forEach((id) => initialExpanded.add(id));
      setExpandedNodeIds(Array.from(initialExpanded));

      // Load ancestor levels so sibling content categories on the path are visible.
      for (const id of [...ancestorIds].reverse()) {
        if (gen !== initGenRef.current) return;
        const live = findCategoryInTree(tree, id);
        if (!live || live.contentCategory || branchChildrenAreLoaded(live)) continue;
        try {
          const loaded = await fetchCategoryForPane(id);
          tree = mergeBranchFetchIntoTree(tree, id, loaded);
          pickerTreeRef.current = tree;
          setPickerTree(tree);
        } catch {
          // User can still expand manually.
        }
      }
      if (gen !== initGenRef.current) return;

      // Prefer the parent-provided default, else first path source with rows.
      const pathGroups = collectAddMatchingContentSourcesAlongAncestorPath(tree, target);
      const pathSourceIds = new Set(
        pathGroups.flatMap((g) => g.sources).map((c) => c.id),
      );
      const initial =
        (defaultId &&
        (pathSourceIds.has(defaultId) ||
          groupsSnapshot.some((g) => g.sources.some((c) => c.id === defaultId)))
          ? defaultId
          : '') ||
        pathGroups.flatMap((g) => g.sources).find((c) => contentCategoryMayHaveRows(c))?.id ||
        '';

      if (!initial || gen !== initGenRef.current) return;

      const src = findCategoryInTree(tree, initial);
      if (src?.contentCategory && !categoryContentsAreLoaded(src)) {
        void ensurePickerNodeLoaded(initial);
      }
      setSourceCategoryId(initial);
      await refreshPreview(target.id, initial);
    })();
    // Intentionally only re-init when the dialog opens for a given target, not when the
    // parent document tree reference changes underneath an open dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetCategory?.id, defaultSourceId, resetReviewState, ensurePickerNodeLoaded, refreshPreview]);

  const exitReview = async (closeDialog: boolean) => {
    // Decisions are applied atomically via Apply; cancelling/closing applies nothing.
    resetReviewState();
    if (!closeDialog && targetCategory && sourceCategoryId) {
      await refreshPreview(targetCategory.id, sourceCategoryId);
    }
    if (closeDialog) {
      onClose();
    }
  };

  const handleCancelReview = () => void exitReview(false);

  const handleClose = () => {
    if (step === 'review') {
      void exitReview(true);
      return;
    }
    resetReviewState();
    onClose();
  };

  const loadSourceContents = async (sourceId: string) => {
    const response = await api.get<Content[]>(
      `/content/list?categoryId=${encodeURIComponent(sourceId)}`,
    );
    const map = new Map<string, Content>();
    for (const row of response.data ?? []) {
      if (row?.id) {
        map.set(row.id, row);
      }
    }
    setSourceContentsById(map);
  };

  const handleStartReview = async () => {
    if (!targetCategory || !sourceCategoryId || matches.length === 0) return;
    setReviewLoading(true);
    setError(null);
    try {
      await loadSourceContents(sourceCategoryId);
      setReviewIndex(0);
      setDecisions(new Map());
      if (applyMode === 'quick') {
        setQuickSelectedIds(
          new Set(matches.map((m) => m.contentId).filter((id): id is string => !!id)),
        );
      } else {
        setQuickSelectedIds(new Set());
      }
      setStep('review');
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, 'Failed to load source rows for review.'));
    } finally {
      setReviewLoading(false);
    }
  };

  const handleApplyAll = async () => {
    if (!targetCategory || !sourceCategoryId) return;
    const ids = matches.map((m) => m.contentId).filter((id): id is string => !!id);
    if (ids.length === 0) return;
    setApplyLoading(true);
    setError(null);
    try {
      const data = await applyAddMatchingContent(
        targetCategory.id,
        sourceCategoryId,
        moveMode === 'move',
        ids,
      );
      const transfers = mapAddMatchingContentTransfers(data.transfers ?? [], moveMode === 'move');
      onSuccess(targetCategory.id, transfers, moveMode === 'move');
      handleClose();
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, 'Failed to add matching content.'));
    } finally {
      setApplyLoading(false);
    }
  };

  const finishReview = (transfers: AddMatchingContentTransfer[]) => {
    if (!targetCategory) return;
    onSuccess(targetCategory.id, transfers, moveMode === 'move');
    handleClose();
  };

  const goToPrevMatch = () => {
    setReviewIndex((i) => Math.max(0, i - 1));
  };

  const goToNextMatch = () => {
    setReviewIndex((i) => Math.min(matches.length - 1, i + 1));
  };

  const recordDecision = (decision: 'accepted' | 'skipped') => {
    const current = matches[reviewIndex];
    if (!current?.contentId) return;
    const id = current.contentId;
    const nextDecisions = new Map(decisions);
    nextDecisions.set(id, decision);
    setDecisions(nextDecisions);
    // Advance one row. We deliberately do NOT jump to the frontier so re-deciding an
    // earlier row keeps the user in place; the "resume point" marker shows where they
    // left off so they can navigate back when ready.
    setReviewIndex((i) => Math.min(i + 1, matches.length - 1));
  };

  const handleRejectCurrent = () => recordDecision('skipped');

  const handleApplyQuickSelected = async () => {
    if (!targetCategory || !sourceCategoryId) return;
    const ids = matches
      .map((m) => m.contentId)
      .filter((id): id is string => !!id && quickSelectedIds.has(id));
    if (ids.length === 0) {
      setError('Select at least one row to apply.');
      return;
    }
    setApplyLoading(true);
    setError(null);
    try {
      const data = await applyAddMatchingContent(
        targetCategory.id,
        sourceCategoryId,
        moveMode === 'move',
        ids,
      );
      const transfers = mapAddMatchingContentTransfers(data.transfers ?? [], moveMode === 'move');
      finishReview(transfers);
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, 'Failed to apply selected rows.'));
    } finally {
      setApplyLoading(false);
    }
  };

  const quickMatchIds = useMemo(
    () => matches.map((m) => m.contentId).filter((id): id is string => !!id),
    [matches],
  );

  const allQuickSelected =
    quickMatchIds.length > 0 && quickSelectedIds.size === quickMatchIds.length;
  const someQuickSelected =
    quickSelectedIds.size > 0 && quickSelectedIds.size < quickMatchIds.length;

  const handleQuickSelectAll = (checked: boolean) => {
    setQuickSelectedIds(checked ? new Set(quickMatchIds) : new Set());
  };

  const toggleQuickMatch = (contentId: string) => {
    setQuickSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(contentId)) {
        next.delete(contentId);
      } else {
        next.add(contentId);
      }
      return next;
    });
  };

  const handleAcceptCurrent = () => recordDecision('accepted');

  // Keyboard shortcuts for step-by-step review: ←/→ navigate, A accepts, S skips. Disabled while a
  // sub-dialog is open, while applying, or when a text field/contenteditable has focus.
  useEffect(() => {
    if (step !== 'review' || applyMode !== 'step') return undefined;
    if (stitchOpen || confirmApplyOpen) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (applyLoading || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable) return;
      const current = matches[reviewIndex];
      const decide = (decision: 'accepted' | 'skipped') => {
        if (!current?.contentId) return;
        e.preventDefault();
        const id = current.contentId;
        setDecisions((prev) => new Map(prev).set(id, decision));
        setReviewIndex((i) => Math.min(i + 1, matches.length - 1));
      };
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          setReviewIndex((i) => Math.max(0, i - 1));
          break;
        case 'ArrowRight':
          e.preventDefault();
          setReviewIndex((i) => Math.min(matches.length - 1, i + 1));
          break;
        case 'a':
        case 'A':
          decide('accepted');
          break;
        case 's':
        case 'S':
          decide('skipped');
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [step, applyMode, stitchOpen, confirmApplyOpen, applyLoading, reviewIndex, matches]);

  const handleApplyAccepted = async () => {
    if (!targetCategory || !sourceCategoryId) return;
    const ids = matches
      .map((m) => m.contentId)
      .filter((id): id is string => !!id && decisions.get(id) === 'accepted');
    if (ids.length === 0) {
      setError('Accept at least one row to apply.');
      return;
    }
    setApplyLoading(true);
    setError(null);
    try {
      const data = await applyAddMatchingContent(
        targetCategory.id,
        sourceCategoryId,
        moveMode === 'move',
        ids,
      );
      const transfers = mapAddMatchingContentTransfers(data.transfers ?? [], moveMode === 'move');
      finishReview(transfers);
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, 'Failed to apply accepted rows.'));
    } finally {
      setApplyLoading(false);
    }
  };

  const currentMatch = matches[reviewIndex];
  const currentDecision = currentMatch?.contentId ? decisions.get(currentMatch.contentId) : undefined;
  const acceptedCount = useMemo(
    () => matches.filter((m) => m.contentId && decisions.get(m.contentId) === 'accepted').length,
    [matches, decisions],
  );
  const skippedCount = useMemo(
    () => matches.filter((m) => m.contentId && decisions.get(m.contentId) === 'skipped').length,
    [matches, decisions],
  );
  const decidedCount = acceptedCount + skippedCount;
  const totalDecidable = useMemo(() => matches.filter((m) => !!m.contentId).length, [matches]);
  const undecidedCount = Math.max(0, totalDecidable - decidedCount);
  // "Resume point" = the row right after the furthest decided row, i.e. where the user left
  // off scanning. Shown as a marker so they can recognize/return to it while paging around.
  const lastDecidedIndex = useMemo(() => {
    let last = -1;
    matches.forEach((m, idx) => {
      if (m.contentId && decisions.has(m.contentId)) last = idx;
    });
    return last;
  }, [matches, decisions]);
  const frontierIndex =
    matches.length > 0 ? Math.min(lastDecidedIndex + 1, matches.length - 1) : 0;
  const hasResumePoint = decidedCount > 0 && lastDecidedIndex < matches.length - 1;
  const atResumePoint = hasResumePoint && reviewIndex === frontierIndex;
  const currentContent = currentMatch ? sourceContentsById.get(currentMatch.contentId) : undefined;
  const isCurrentEdited = !!currentMatch?.contentId && editedRowIds.has(currentMatch.contentId);
  // Edited rows are stored as plain text, so their rich preview is naturally cleared.
  const displayHtml = currentContent?.richTextHtml?.trim()
    ? prepareContentRichPreviewHtml(currentContent.richTextHtml)
    : null;
  const displayText = currentMatch
    ? matchPreviewText(currentMatch, currentContent, 4000)
    : '(empty)';
  const displayIsRTL = /[\u0590-\u05FF\u0600-\u06FF]/.test(displayText);

  // Neighbours follow the review navigation order (the rows reached with Back / Next), so the
  // stitch prev/next stay consistent with what the user is stepping through.
  // Read the freshest subcase for a match (edited rows in sourceContentsById win over the preview).
  const subcaseForMatch = (match?: { contentId?: string; subcase?: string }): string =>
    (match?.contentId ? sourceContentsById.get(match.contentId)?.subcase : undefined) ??
    match?.subcase ??
    '';
  const currentSubcase = subcaseForMatch(currentMatch);

  const prevMatch = reviewIndex > 0 ? matches[reviewIndex - 1] : undefined;
  const nextMatch = reviewIndex < matches.length - 1 ? matches[reviewIndex + 1] : undefined;
  // A side is only offered when that neighbour shares the current row's subcase — borrowing across
  // subcases doesn't make sense (mirrors the content-pane Adjust text rule).
  const hasPrevNeighbour = !!prevMatch?.contentId && subcaseForMatch(prevMatch) === currentSubcase;
  const hasNextNeighbour = !!nextMatch?.contentId && subcaseForMatch(nextMatch) === currentSubcase;
  const prevNeighbourText = hasPrevNeighbour
    ? (sourceContentsById.get(prevMatch!.contentId)?.text ?? prevMatch!.textPreview ?? '')
    : '';
  const nextNeighbourText = hasNextNeighbour
    ? (sourceContentsById.get(nextMatch!.contentId)?.text ?? nextMatch!.textPreview ?? '')
    : '';
  const canStitchCurrent =
    applyMode === 'step' &&
    !!currentMatch?.contentId &&
    (hasPrevNeighbour || hasNextNeighbour);

  const currentBaseText = (currentContent?.text ?? currentMatch?.textPreview ?? '').toString();

  const openStitchDialog = () => {
    if (!currentMatch?.contentId) return;
    setStitchRowId(currentMatch.contentId);
    setStitchOpen(true);
  };

  // Persist a source row's text in place (POST /content with an id performs an update). Rich
  // text/delta are cleared because the stitched text is plain; attachments are preserved.
  const persistSourceRowText = async (rowId: string, newText: string): Promise<Content> => {
    const existing = sourceContentsById.get(rowId);
    const payload: Record<string, unknown> = {
      parentId: sourceCategoryId,
      id: rowId,
      subcase: existing?.subcase ?? currentMatch?.subcase ?? '',
      text: newText,
      richTextHtml: '',
      richTextDelta: '',
      ...(typeof existing?.attachmentsJson === 'string'
        ? { attachmentsJson: existing.attachmentsJson }
        : {}),
    };
    const response = await api.post<Content>('/content', payload);
    const updated = response.data;
    setSourceContentsById((prev) => {
      const next = new Map(prev);
      next.set(rowId, updated);
      return next;
    });
    onSourceRowEdited?.(sourceCategoryId, updated, existing);
    return updated;
  };

  const handleStitchSave = async (newText: string) => {
    const rowId = stitchRowId;
    if (!rowId) {
      setStitchOpen(false);
      return;
    }
    const beforeText = (sourceContentsById.get(rowId)?.text ?? currentMatch?.textPreview ?? '')
      .toString();
    if (!newText.trim() || newText === beforeText) {
      setStitchOpen(false);
      return;
    }
    // persistSourceRowText throws on failure — the AdjustTextDialog surfaces the error and stays open.
    await persistSourceRowText(rowId, newText);
    setOriginalTextById((prev) => (prev.has(rowId) ? prev : new Map(prev).set(rowId, beforeText)));
    setEditedRowIds((prev) => new Set(prev).add(rowId));
    setStitchOpen(false);
  };

  const revertCurrentEdit = async () => {
    const rowId = currentMatch?.contentId;
    if (!rowId) return;
    const original = originalTextById.get(rowId);
    if (original === undefined) return;
    setApplyLoading(true);
    setError(null);
    try {
      await persistSourceRowText(rowId, original);
      setEditedRowIds((prev) => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
      setOriginalTextById((prev) => {
        const next = new Map(prev);
        next.delete(rowId);
        return next;
      });
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, 'Failed to revert the edited text.'));
    } finally {
      setApplyLoading(false);
    }
  };

  const matchCount = matches.length;
  const thresholdPercent = Math.round(similarityThreshold * 100);
  const matchCountPercent = Math.round(
    (previewedThreshold ?? similarityThreshold) * 100,
  );
  const thresholdIsSessionDefault =
    Math.abs(similarityThreshold - sessionDefaultThresholdRef.current) < 0.0005;
  const reviewProgress =
    applyMode === 'step' && matches.length > 0
      ? (decidedCount / matches.length) * 100
      : 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          // Don't abandon via stray backdrop click — exit via Cancel / Apply (or Escape).
          if (reason === 'backdropClick') {
            return;
          }
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
        PaperComponent={DraggablePaper}
        slotProps={{ backdrop: { sx: { backgroundColor: 'transparent' } } }}
      >
        <DialogTitle data-drag-handle="true" sx={{ cursor: 'move' }}>
          {step === 'setup'
            ? 'Add matching rows from another category (AI)'
            : applyMode === 'quick'
              ? 'Quick review — select rows'
              : 'Review matching rows'}
        </DialogTitle>
        <DialogContent>
          {step === 'setup' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Destination: <strong>{targetCategory?.name ?? 'this category'}</strong>
              </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                <Tooltip title={getTooltipTextForState('add-matching-content', true)} placement="top">
                  <span>Copy or move from</span>
                </Tooltip>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Expand folders and select a content category
                {suggestedSourceExamples && suggestedSourceExamples.length > 0
                  ? ` (e.g. ${suggestedSourceExamples.join(' or ')})`
                  : ''}
                .
              </Typography>
              {documentRoot && (
                <Box
                  sx={{
                    maxHeight: 280,
                    overflow: 'auto',
                    border: theme => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 0.5,
                  }}
                >
                  <TreeView
                    key={`${documentRoot.id}-${targetCategory?.id}`}
                    defaultCollapseIcon={<ExpandMoreIcon />}
                    defaultExpandIcon={<ChevronRightIcon />}
                    expanded={expandedNodeIds}
                    onNodeToggle={handleNodeToggle}
                    selected={sourceCategoryId || ''}
                    onNodeSelect={handleSourceTreeSelect}
                  >
                    {renderSourceTreeNode(documentRoot)}
                  </TreeView>
                </Box>
              )}
            </Box>

              {!sourceCategoryId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {CHOOSE_SOURCE_MESSAGE}
                </Alert>
              )}

              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Action
                </Typography>
                <RadioGroup
                  row
                  value={moveMode}
                  onChange={(e) => setMoveMode(e.target.value as 'copy' | 'move')}
                >
                  <FormControlLabel value="copy" control={<Radio />} label="Copy rows" />
                  <FormControlLabel value="move" control={<Radio />} label="Move rows" />
                </RadioGroup>
              </FormControl>

              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <Tooltip title={getTooltipTextForState('add-matching-apply-mode', true)} placement="top">
                    <span>How to apply</span>
                  </Tooltip>
                </Typography>
                <RadioGroup
                  row
                  value={applyMode}
                  onChange={(e) => setApplyMode(e.target.value as ApplyMode)}
                >
                  <FormControlLabel value="all" control={<Radio />} label="Apply all matches" />
                  <FormControlLabel
                    value="step"
                    control={<Radio />}
                    label="Step-by-step review"
                  />
                  <FormControlLabel
                    value="quick"
                    control={<Radio />}
                    label="Quick review"
                  />
                </RadioGroup>
              </FormControl>

              <Tooltip title={getTooltipTextForState('add-matching-show-similarity-score', true)}>
                <span>
                  <FormControlLabel
                    sx={{ mb: 2, display: 'block' }}
                    control={
                      <Checkbox
                        checked={addMatchingShowSimilarityScore}
                        onChange={async (e) => {
                          const checked = e.target.checked;
                          updateSettings({ addMatchingShowSimilarityScore: checked });
                          try {
                            await api.put('/search/settings', {
                              search: { addMatchingShowSimilarityScore: checked },
                            });
                          } catch (err) {
                            console.error('Error updating add matching settings:', err);
                          }
                        }}
                        size="small"
                      />
                    }
                    label="Show similarity scores on matched rows"
                  />
                </span>
              </Tooltip>

              <Box sx={{ mb: 2 }}>
                <Tooltip
                  title={getTooltipTextForState('add-matching-similarity-threshold', true)}
                  placement="top"
                >
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Similarity threshold: {thresholdPercent}%
                    {thresholdIsSessionDefault ? ' (default)' : ''}
                  </Typography>
                </Tooltip>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Lower = more matches. Release the slider or click Retry matching to update the
                  count. Does not change your Settings default.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Slider
                    aria-label="Similarity threshold"
                    value={similarityThreshold}
                    onChange={(_, value) => {
                      const next = clampSimilarityThreshold(
                        Array.isArray(value) ? value[0] : value,
                      );
                      setSimilarityThreshold(next);
                      similarityThresholdRef.current = next;
                    }}
                    onChangeCommitted={(_, value) => {
                      applySimilarityThreshold(Array.isArray(value) ? value[0] : value);
                    }}
                    min={0}
                    max={1}
                    step={0.01}
                    size="small"
                    valueLabelDisplay="off"
                    sx={{ flex: 1, minWidth: 120 }}
                  />
                  <Button
                    size="small"
                    onClick={() => {
                      if (targetCategory && sourceCategoryId) {
                        void refreshPreview(
                          targetCategory.id,
                          sourceCategoryId,
                          similarityThreshold,
                        );
                      }
                    }}
                    disabled={!sourceCategoryId || previewLoading}
                  >
                    Retry matching
                  </Button>
                  <Button
                    size="small"
                    onClick={() => applySimilarityThreshold(sessionDefaultThresholdRef.current)}
                    disabled={thresholdIsSessionDefault}
                  >
                    Reset
                  </Button>
                </Box>
              </Box>

              {showSubcaseFilter && (
                <Box sx={{ mb: 2 }}>
                  <Tooltip
                    title={getTooltipTextForState('add-matching-subcase-filter', true)}
                    placement="top"
                  >
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Subcases to include
                    </Typography>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.75 }}
                  >
                    Uncheck subcases whose matching rows should be left out.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                    <Button
                      size="small"
                      onClick={() => setSelectedSubcases(new Set(matchSubcases))}
                    >
                      All subcases
                    </Button>
                    <Button size="small" onClick={() => setSelectedSubcases(new Set())}>
                      None
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      maxHeight: 160,
                      overflow: 'auto',
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      px: 1,
                    }}
                  >
                    {matchSubcases.map((subcase) => {
                      const isRtl = /[\u0590-\u05FF\u0600-\u06FF]/.test(subcase);
                      return (
                        <FormControlLabel
                          key={subcase || '(none)'}
                          sx={{ display: 'flex', ml: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              inputProps={{ 'aria-label': subcase || 'no subcase' }}
                              checked={selectedSubcases.has(subcase)}
                              onChange={(e) => {
                                setSelectedSubcases((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(subcase);
                                  else next.delete(subcase);
                                  return next;
                                });
                              }}
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              dir={isRtl ? 'rtl' : 'ltr'}
                              sx={{ textAlign: isRtl ? 'right' : 'left' }}
                            >
                              {subcase || '(no subcase)'}
                            </Typography>
                          }
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}

              {sourceCategoryId && (
                <Alert
                  severity={previewLoading ? 'info' : matchCount > 0 ? 'success' : 'warning'}
                  icon={previewLoading ? <CircularProgress size={18} /> : undefined}
                  sx={{ mb: 2 }}
                >
                  {previewLoading
                    ? 'Checking matches…'
                    : matchCount === 0
                      ? allMatches.length > 0
                        ? 'No matching rows in the selected subcases.'
                        : 'No matching rows found in the selected source.'
                      : `${matchCount} matching row${matchCount === 1 ? '' : 's'} found at ${matchCountPercent}%.`}
                </Alert>
              )}
            </>
          )}

          {step === 'review' && applyMode === 'step' && currentMatch && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Destination: <strong>{targetCategory?.name ?? 'this category'}</strong>
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'nowrap',
                  mb: 1,
                  alignItems: 'center',
                  overflow: 'hidden',
                }}
              >
                <Chip size="small" label={`${reviewIndex + 1} of ${matches.length}`} />
                {/* The resume ("You stopped here") chip already conveys the undecided state,
                    so skip the redundant "not decided" chip on that row. */}
                {!(atResumePoint && !currentDecision) && (
                  <Chip
                    size="small"
                    variant={currentDecision ? 'filled' : 'outlined'}
                    color={
                      currentDecision === 'accepted'
                        ? 'success'
                        : currentDecision === 'skipped'
                          ? 'default'
                          : 'warning'
                    }
                    label={
                      currentDecision === 'accepted'
                        ? `This row: ${moveMode === 'move' ? 'Move' : 'Copy'}`
                        : currentDecision === 'skipped'
                          ? 'This row: Skip'
                          : 'This row: not decided'
                    }
                  />
                )}
                {hasResumePoint && (
                  <Chip
                    size="small"
                    color="info"
                    variant={atResumePoint ? 'filled' : 'outlined'}
                    clickable={!atResumePoint}
                    onClick={atResumePoint ? undefined : () => setReviewIndex(frontierIndex)}
                    label={
                      atResumePoint
                        ? 'You stopped here'
                        : `Resume at #${frontierIndex + 1}`
                    }
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={reviewProgress}
                  sx={{ flexGrow: 1 }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  <Box component="span" sx={{ color: 'success.main' }}>{acceptedCount} accepted</Box>
                  {' · '}
                  {skippedCount} skipped
                  {undecidedCount > 0 && (
                    <>
                      {' · '}
                      <Box component="span" sx={{ color: 'warning.main' }}>{undecidedCount} undecided</Box>
                    </>
                  )}
                </Typography>
              </Box>
              {currentMatch.subcase && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 1, textAlign: displayIsRTL ? 'right' : 'left' }}
                  dir={displayIsRTL ? 'rtl' : 'ltr'}
                >
                  Subcase: {currentMatch.subcase}
                </Typography>
              )}
              {addMatchingShowSimilarityScore && formatMatchSimilarityPercent(currentMatch.score) && (
                <Typography variant="caption" color="primary" display="block" sx={{ mb: 1 }}>
                  Similarity: {formatMatchSimilarityPercent(currentMatch.score)}
                </Typography>
              )}
              <Typography
                variant="body2"
                dir={displayIsRTL ? 'rtl' : 'ltr'}
                sx={{
                  whiteSpace: 'pre-wrap',
                  maxHeight: 240,
                  overflow: 'auto',
                  mb: 2,
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  border: 2,
                  borderColor:
                    currentDecision === 'accepted'
                      ? 'success.main'
                      : currentDecision === 'skipped'
                        ? 'divider'
                        : atResumePoint
                          ? 'info.main'
                          : 'warning.main',
                  textAlign: displayIsRTL ? 'right' : 'left',
                }}
              >
                {displayText}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                {displayHtml && (
                  <Button size="small" onClick={() => setRichPreviewOpen(true)}>
                    Rich preview
                  </Button>
                )}
                {canStitchCurrent && (
                  <Tooltip title={getTooltipTextForState('adjust-text-content', true)}>
                    <Button size="small" onClick={openStitchDialog}>
                      {isCurrentEdited ? 'Edit text…' : 'Adjust text…'}
                    </Button>
                  </Tooltip>
                )}
                {isCurrentEdited && (
                  <>
                    <Chip size="small" color="info" variant="outlined" label="Edited" />
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => void revertCurrentEdit()}
                      disabled={applyLoading}
                    >
                      Revert
                    </Button>
                  </>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block">
                ← Back · → Next · A Accept · S Skip — nothing applied until Apply.
              </Typography>
            </Box>
          )}

          {step === 'review' && applyMode === 'quick' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Destination: <strong>{targetCategory?.name ?? 'this category'}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select rows to {moveMode === 'move' ? 'move' : 'copy'} ({quickSelectedIds.size} of{' '}
                {matches.length} selected).
              </Typography>
              <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
                <ListItem disablePadding sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <ListItemButton
                    onClick={() => handleQuickSelectAll(!allQuickSelected)}
                    dense
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={allQuickSelected}
                        indeterminate={someQuickSelected}
                        disabled={quickMatchIds.length === 0}
                        tabIndex={-1}
                        disableRipple
                        onChange={(e) => handleQuickSelectAll(e.target.checked)}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Select all" />
                  </ListItemButton>
                </ListItem>
                {matches.map((m) => {
                  const id = m.contentId;
                  if (!id) return null;
                  const content = sourceContentsById.get(id);
                  const similarityLabel =
                    addMatchingShowSimilarityScore && formatMatchSimilarityPercent(m.score)
                      ? `Similarity: ${formatMatchSimilarityPercent(m.score)}`
                      : undefined;
                  return (
                    <ListItem key={id} disablePadding>
                      <ListItemButton onClick={() => toggleQuickMatch(id)} dense>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            checked={quickSelectedIds.has(id)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={m.subcase || '(no subcase)'}
                          secondary={
                            similarityLabel
                              ? `${similarityLabel} · ${matchPreviewText(m, content, 120)}`
                              : matchPreviewText(m, content, 120)
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {step === 'setup' ? (
            <>
              <Button onClick={handleClose}>Cancel</Button>
              {applyMode === 'all' ? (
                <Button
                  variant="contained"
                  onClick={() => void handleApplyAll()}
                  disabled={
                    applyLoading ||
                    previewLoading ||
                    !sourceCategoryId ||
                    matchCount === 0
                  }
                >
                  {applyLoading ? 'Applying…' : `Apply ${matchCount} row${matchCount === 1 ? '' : 's'}`}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => void handleStartReview()}
                  disabled={
                    reviewLoading ||
                    previewLoading ||
                    !sourceCategoryId ||
                    matchCount === 0
                  }
                >
                  {reviewLoading ? 'Loading…' : 'Start review'}
                </Button>
              )}
            </>
          ) : (
            <>
              <Button onClick={handleCancelReview}>Cancel review</Button>
              {applyMode === 'quick' ? (
                <Button
                  variant="contained"
                  onClick={() => void handleApplyQuickSelected()}
                  disabled={applyLoading || quickSelectedIds.size === 0}
                >
                  {applyLoading
                    ? 'Applying…'
                    : `Apply ${quickSelectedIds.size} selected`}
                </Button>
              ) : (
                <>
                  <Tooltip title="Back (←)">
                    <span>
                      <Button onClick={goToPrevMatch} disabled={applyLoading || reviewIndex === 0}>
                        Back
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Next (→)">
                    <span>
                      <Button
                        onClick={goToNextMatch}
                        disabled={applyLoading || reviewIndex >= matches.length - 1}
                      >
                        Next
                      </Button>
                    </span>
                  </Tooltip>
                  <Box sx={{ flexGrow: 1 }} />
                  <Tooltip title="Skip (S)">
                    <span>
                      <Button
                        onClick={handleRejectCurrent}
                        color="inherit"
                        variant={currentDecision === 'skipped' ? 'contained' : 'outlined'}
                        disabled={applyLoading}
                      >
                        Skip
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={`${moveMode === 'move' ? 'Move' : 'Copy'} (A)`}>
                    <span>
                      <Button
                        onClick={handleAcceptCurrent}
                        variant={currentDecision === 'accepted' ? 'contained' : 'outlined'}
                        disabled={applyLoading}
                      >
                        {moveMode === 'move' ? 'Move' : 'Copy'}
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => {
                      if (undecidedCount > 0) {
                        setConfirmApplyOpen(true);
                      } else {
                        void handleApplyAccepted();
                      }
                    }}
                    disabled={applyLoading || acceptedCount === 0}
                  >
                    {applyLoading ? 'Applying…' : `Apply ${acceptedCount}`}
                  </Button>
                </>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
      <ContentRichPreviewDialog
        open={richPreviewOpen}
        onClose={() => setRichPreviewOpen(false)}
        html={displayHtml ?? ''}
        title={currentMatch?.subcase || 'Source row preview'}
      />
      <AdjustTextDialog
        open={stitchOpen}
        onClose={() => setStitchOpen(false)}
        currentText={
          (stitchRowId ? sourceContentsById.get(stitchRowId)?.text : undefined) ?? currentBaseText
        }
        prevText={hasPrevNeighbour ? prevNeighbourText : undefined}
        nextText={hasNextNeighbour ? nextNeighbourText : undefined}
        onSave={handleStitchSave}
      />
      <Dialog
        open={confirmApplyOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          setConfirmApplyOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Apply accepted rows?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {undecidedCount} row{undecidedCount === 1 ? '' : 's'} weren’t reviewed and will be
            skipped. Apply {acceptedCount} accepted row{acceptedCount === 1 ? '' : 's'}
            {moveMode === 'move' ? ' (move)' : ' (copy)'}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApplyOpen(false)}>Keep reviewing</Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setConfirmApplyOpen(false);
              void handleApplyAccepted();
            }}
          >
            Apply {acceptedCount}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddMatchingContentDialog;
